import { Component, ElementRef, Inject, Injectable, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { MD_DIALOG_DATA, MdDialog, MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { AppConfig } from './../../Common/appConfiguration';
import { AppMessage } from './../../Common/appMessage';
import { AppGlobals } from './../../Common/Services/app.global';
import { SharedService } from './../../Common/Services/shared.service';
import { Utility } from './../../Common/Services/utility';
import { DialogConfirmComponentComponent } from './../../Components/dialog-confirm-component/dialog-confirm-component.component';
import { userRole_candidate } from './../../Common/appConstant';
import { examTypeOrder } from './../../Common/appConstant';
import { Enum } from "../../utility/enum.enum";
import { ExamType } from 'app/Common/enums/exam-type.enum';
import { FeatureFlagService, ToggleName } from 'app/Common/Services/feature-flag.service';

@Component({
  selector: 'app-dialog-exam-details',
  templateUrl: './dialog-exam-details.component.html',
  styleUrls: ['./dialog-exam-details.component.css'],
  providers: [FeatureFlagService]
})

@Injectable()
export class DialogExamDetailsComponent implements OnInit, OnDestroy {
  dashboardModel: any;
  timeZoneFilteredOptions: any;
  virtualLocationCountryFilteredOptions: any;
  additionalInfoForm: FormGroup;
  additionalInfoRequired: boolean;
  originalSelectedExamModel: any;
  showExamOptionsForReschedule: boolean;
  examObjectivesIsEnabled: boolean;

  constructor(private _appGlobals: AppGlobals, public _utility: Utility, private elem: ElementRef
    , private router: Router, private _sharedService: SharedService, public _dialog: MdDialog
    , public dialogRef: MdDialogRef<DialogExamDetailsComponent>, @Inject(MD_DIALOG_DATA) public data: any
    , private _vcr: ViewContainerRef, public _appMessage: AppMessage, public _appConfig: AppConfig
    , private featureFlags: FeatureFlagService) {
    this.dashboardModel = this.data;
    this.additionalInfoForm = new FormGroup({
      timeZoneControl: new FormControl(""),
      countryControl: new FormControl("")
    });
  }

  ngOnInit() {
    this.dashboardModel.selectedExamModel.examOptions.sort(
      (a, b) => examTypeOrder.indexOf(a.examType) < examTypeOrder.indexOf(b.examType) ? -1 : 1
    );
    this.dashboardModel.availableExamTypes = this.dashboardModel.selectedExamModel.examOptions
      .map(({ examType }) => examType as ExamType)
      .reduce((accumulator: Set<ExamType>, value: ExamType) => accumulator.add(value), new Set());
    this.dashboardModel.availableExams = [];
    this.dashboardModel.selectedExamType = null;
    this.examObjectivesIsEnabled = false
    this.featureFlags.on("ready", () => {
      this.examObjectivesIsEnabled = this.featureFlags.isEnabled(ToggleName.ENABLE_EXAM_OBJECTIVES);
    });
    this.featureFlags.on("update", () => {
      this.examObjectivesIsEnabled = this.featureFlags.isEnabled(ToggleName.ENABLE_EXAM_OBJECTIVES);
    });
    this.featureFlags.start();


    this.originalSelectedExamModel = Object.assign({}, this.dashboardModel.selectedExamModel);
    this._appGlobals.selectedExamModel = null;
    this._appGlobals.selectedExamLocationModel = null;
    this.dashboardModel.isCountrySelected = false;
    this.dashboardModel.submitInProcess = false;
    this.showExamOptionsForReschedule = false;
    this.dashboardModel.selectedCountry = null;
    this.dashboardModel.selectedExam = null;
    this.additionalInfoRequired = false;
    this.setCountryList();
    this.setTimeZoneList();
  }

  ngOnDestroy(): void {
    this.featureFlags.stop();
  }

  closeDialog(dialogResult: any = 0) {
    this.dialogRef.close(dialogResult);
  }

  getObjectivesUrl() {
    const versionlessSku = this.originalSelectedExamModel.examCode;
    const versionedSku = this.dashboardModel.selectedExamModel.examCode;
    const url = new URL(`${versionlessSku}?tab=objectives&sku=${versionedSku}`, "https://rol.redhat.com/rol/app/exam/");
    return url.toString();
  }

  prepopulateVirtualCountryList() {
    if (this._utility.usesVirtualLocation(this.dashboardModel.selectedExamModel)
      && this.dashboardModel.selectedExamModel.locationCountryName) {

      for (let countryOption of this._sharedService.virtualLocationCountryList) {
        if (countryOption.name == this.dashboardModel.selectedExamModel.locationCountryName) {
          this.additionalInfoForm.controls.countryControl.setValue(countryOption);
          break;
        }
      }
    }
  }

  convertExamTypeToDisplay(examType: ExamType): string {
    return ExamType.toString(examType);
  }

  selectCountry() {
    this.dashboardModel.isCountrySelected = true;
  }

  selectExamType() {
    // This is disgusting but I'm doing this because at the time this callback is invoked, the model change hasn't actually happened.
    // setTimeout 0, schedules a macrotask which will execute after the model has been updated
    setTimeout(() => {
      this.dashboardModel.selectedExamModel.examType = this.dashboardModel.selectedExamType;
      this.dashboardModel.availableExams = this.dashboardModel.selectedExamModel.examOptions
        .filter(({ examType }) => (examType as ExamType) === this.dashboardModel.selectedExamType)
    }, 0)
  }

  setProfile(msg = null) {
    this._utility.addLoaderCount();
    this._sharedService.getUserProfile()
      .subscribe((data: any) => {
        this._utility.onSuccessCallback(data, msg, () => {
          let resultData = data.response;
          this.dashboardModel.userInfo = resultData;
          let userRole = this._appGlobals.userInfo.getValue().userRole; // get role from userinfo object anf If role is Candidate then only Update UserInfo object
          if (userRole == userRole_candidate) {
            this._appGlobals.userInfo.next(resultData);
          }

          this.setUploadIdSection();
        });
      }, error => {
        this._utility.onErrorCallback(error);
      });
  }

  canChangeType() {
    if (!this.effectiveExamStatusIs('SCHEDULED'))
      return false;

    let opts = this.dashboardModel.selectedExamModel.examOptions;
    let selectedType = this.dashboardModel.selectedExamModel.examType;
    let thereIsMoreThanOneOption = opts.length > 1;
    let theOnlyOptIsntSelected = opts.length == 1 && opts[0].examType != selectedType;

    return thereIsMoreThanOneOption || theOnlyOptIsntSelected;
  }

  // Deprecated: ID upload is now handled externally via Mirilla (https://red.ht/exam_id_upload).
  // The backend user profile endpoint now checks Mirilla API for ID verification status.

  /**
   * Show / Hide ID Upload section
   * Get CountryID from userinfo object and match by country List (fetched from countries.json api)
   * If candidate countryID is in countries List then show this section (this.dashboardModel.isCountrySelected = true) else hide.
  */
  setUploadIdSection() {
    this.dashboardModel.isCountrySelected = false;

    if (this.dashboardModel.userInfo.countryId > 0) {
      let countryList = this.dashboardModel.countryList;
      let countryID = this.dashboardModel.userInfo.countryId;

      if (countryList && countryList.length > 0 && countryID > 0) {
        let selCountry = countryList.find(function(country) {
          return country.id === countryID;
        });

        if (selCountry && selCountry != null) {
          this.dashboardModel.selectedCountry = selCountry;
          this.dashboardModel.isCountrySelected = true;
        }
      }
    }
  }

  setTimeZoneList() {
    // get timeZone data and bind with AutoComplete
    if (this._sharedService.timeZoneList) {
      this._utility.setDataInComboField("timeZone", null, this.additionalInfoForm.controls.timeZoneControl, this);
      this.prepopulateTimeZoneList();
    } else {
      this._utility.addLoaderCount();
      this._sharedService.getComboFieldsData(Enum.TimeZone, (response) => {
        this._utility.removeLoaderCount();
        this._sharedService.timeZoneList = response.response;
        this._utility.setDataInComboField("timeZone", null, this.additionalInfoForm.controls.timeZoneControl, this);
        if (this._utility.usesVirtualLocation(this.dashboardModel.selectedExamModel)) {
          this.prepopulateTimeZoneList();
        }
      });
    }
  }

  prepopulateTimeZoneList() {
    let timeZone = this.dashboardModel.selectedExamModel.timeZone

    if (!timeZone || timeZone == "UTC")
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    for (let timeZoneOption of this._sharedService.timeZoneList) {
      if (timeZoneOption.name == timeZone) {
        this.additionalInfoForm.controls.timeZoneControl.setValue(timeZoneOption);
        break;
      }
    }
  }

  // To get country list to upload ids
  setCountryList() {
    this.dashboardModel.countryList = [];
    this._utility.addLoaderCount();
    this._sharedService.searchCountries("")
      .subscribe((data: any) => {
        this._utility.onSuccessCallback(data, null, () => {
          this.dashboardModel.countryList = data.response.results;
          this._sharedService.virtualLocationCountryList = this.dashboardModel.countryList;
          this._utility.setDataInComboField('virtualLocationCountry', null, this.additionalInfoForm.controls.countryControl, this);
          this.setProfile();
          this.prepopulateVirtualCountryList();
        });
      }, error => {
        this._utility.onErrorCallback(error);
      });
  }

  resetExamSelection() {
    this.dashboardModel.selectedExamModel = Object.assign({}, this.originalSelectedExamModel);
    this.additionalInfoRequired = false;
    this.showExamOptionsForReschedule = false;
  }

  effectiveExamStatusIs(status) {
    return this._utility.effectiveExamStatusIs(this.dashboardModel.selectedExamModel, status);
  }

  effectiveExamStatusIsIn(statuses) {
    return this._utility.effectiveExamStatusIsIn(this.dashboardModel.selectedExamModel, statuses);
  }

  selectExamOption() {
    setTimeout(() => {
      this.dashboardModel.selectedExamModel = Object.assign(
        {}, this.dashboardModel.selectedExamModel, this.dashboardModel.selectedExam
      );
    }, 0)
  }

  scheduleExam() {
    let isReschedule = !this.effectiveExamStatusIs('EXAMSTOSCHEDULE');
    this.selectExamOption();

    // Show more form options
    if (this._utility.usesVirtualLocation(this.dashboardModel.selectedExamModel)
      && !this.additionalInfoRequired) {
      this.additionalInfoRequired = true;
      return;
    }

    // selected country from dropdown , using this to center map if someone block location
    this._utility.selectedCountry = this.dashboardModel.selectedCountry.name;
    let instance = this._dialog.openDialogs[0];
    this._dialog.openDialogs.pop();
    let msg;
    let timezone;
    let inputDate;
    let model = this.dashboardModel.selectedExamModel;

    if (this._utility.usesVirtualLocation(model)) {
      let formValues = this.additionalInfoForm.getRawValue();

      if (formValues.timeZoneControl && formValues.timeZoneControl.id) {
        model.virtualLocationTimeZone = formValues.timeZoneControl;
      } else {
        this._utility.showDialogMsg(msg, "ERROR", this._appMessage.getItem['UMsg00094']);
        return;
      }

      if (formValues.countryControl && formValues.countryControl.id) {
        model.virtualLocationCountry = formValues.countryControl;
        let examTypeNotSupported = model.virtualLocationCountry.unsupportedExamTypes.indexOf(this.dashboardModel.selectedExamModel.examType) > -1;

        if (examTypeNotSupported) {
          this._utility.showDialogMsg(msg, "ERROR", this._appMessage.getItem['UMsg00096']);
          return;
        }
      } else {
        this._utility.showDialogMsg(msg, "ERROR", this._appMessage.getItem['UMsg00095']);
        return;
      }
    }

    if (isReschedule) {
      msg = this._appMessage.getItem["UMsg00003"];
      timezone = model.timeZone;
      inputDate = this._utility.convertToDateTime(model.examStartDateTime);
    } else {
      msg = this._appMessage.getItem["UMsg00001"];
      timezone = "UTC";
      inputDate = this._utility.convertToDateTime(model.eligibilityExpirationDate);
    }

    if (!model.examIsActive) {
      this._utility.showDialogMsg(msg, "ERROR", this._appMessage.getItem['UMsg00071']);
      return;
    }

    // check for time difference
    let inputDateStr = this._utility.convertToDateStr(inputDate, 'dt');
    let params = "?timezone=" + timezone + '&inputDate=' + inputDateStr;
    this._utility.addLoaderCount();
    this._sharedService.getTimeDiff(params).subscribe(
      (data: any) => {
        this._utility.onSuccessCallback(data, null, () => {
          // if time difference is <= 24 hr(s) show the validation message, otherwise proceed for Rescheduling
          if (parseFloat(data.response.timeDiff) > 24) {
            model.isReschedule = isReschedule;
            this._appGlobals.selectedExamModel = model;
            this.closeDialog();
            this.router.navigate(['/ScheduleExamLocation']);
          } else if (isReschedule) {
            this._utility.showDialogMsg(this._appMessage.getItem["UMsg00004"], "ERROR", this._appMessage.getItem['UMsg00014']);
          } else {
            this._utility.showDialogMsg(this._appMessage.getItem["UMsg00002"], "ERROR", this._appMessage.getItem['UMsg00015']);
          }
        });
      }, error => {
        this.closeDialog();
        this._utility.onErrorCallback(error);
      }
    );

    this._dialog.openDialogs.push(instance);
  }

  cancelExam() {
    let model = this.dashboardModel.selectedExamModel;
    let timezone = model.timeZone;
    let inputDate = this._utility.convertToDateTime(model.examStartDateTime);
    let params = "?timezone=" + timezone;
    let inputDateStr = this._utility.convertToDateStr(inputDate, 'dt');
    params += '&inputDate=' + inputDateStr;

    // Check for time difference first before perform cancel
    this._sharedService.getTimeDiff(params).subscribe(
      (data: any) => {
        this._utility.onSuccessCallback(data, null, () => {
          let msg;
          if (parseFloat(data.response.timeDiff) > 24) {
            msg = this._appMessage.getItem["UMsg00092"];
          } else {
            msg = this._appMessage.getItem["UMsg00012"];
          }

          this.callCancel(msg);
        });
      }, error => {
        this.closeDialog();
        this._utility.onErrorCallback(error);
      }
    );
  }

  // proceed for canceling the exam
  callCancel(message: string) {
    let instance = this._dialog.openDialogs[0];
    this._dialog.openDialogs.pop(); // clear all popop instance from openDialogs object
    let confirmDialogMsgModel = {
      modelType: "CONFIRMATION",
      msgTitle: this._appMessage.getItem['Common007'],//'Are you sure?',
      msg: message,
      isConfirmBox: true,
      confirmBtnCaption: this._appMessage.getItem['UMsg00013'],//'Cancel Exam',
      cancelBtnCaption: this._appMessage.getItem['Common006']// 'Not sure'
    };

    let confirmDialogMsgRef = this._dialog.open(DialogConfirmComponentComponent, {
      data: confirmDialogMsgModel
    });

    confirmDialogMsgRef.afterClosed().subscribe(result => {
      if (result == "1") {
        let params = {
          eligibilityCode: this.dashboardModel.selectedExamModel.eligibilityCode
        }
        this._utility.addLoaderCount();
        this._sharedService.cancelExam(params).subscribe(data => {
          this._utility.onSuccessCallback(data, null, () => {
            // exam has been canceled successfully
            // update the exams list at dashboard and
            // show the success message
            let SuccessMsgDialogRefModel = {
              modelType: "SUCCESS",
              msgTitle: this._appMessage.getItem["Common008"],//'Success',
              msg: this._appMessage.getItem["UMsg00010"],
              showDefaultBtn: true,
              defaultBtnCaption: this._appMessage.getItem["Common009"],//'Continue',
              isConfirmBox: false
            };
            let SuccessMsgDialogRef = this._dialog.open(DialogConfirmComponentComponent,
              {
                data: SuccessMsgDialogRefModel
              });
            this.closeDialog(1);
          });
        }, error => {
          this.closeDialog(1);
          this._utility.onErrorCallback(error);
        }, () => { });
      }
    });
    this._dialog.openDialogs.push(instance);//popped instance pushed again to maintain its normal behavior
  }

  getExamDisplayCode(examCodeName: string) {
    return examCodeName.substring(0, examCodeName.indexOf(":"));
  }

  getExamDisplayName(examCodeName) {
    return examCodeName;
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/Components/dialog-exam-details/dialog-exam-details.component.ts